/**
 * #1150 — Game Detail's Novig lane: the selected team's outcome, its best
 * bid/ask in native probability, and the manual ticket bound to that outcome.
 *
 * The team slot comes from the host (the same away/home selection the K/P
 * form uses), so one selected market/ticket is kept across venue tabs. A
 * level click seeds that outcome's ticket price; nothing else does.
 */
import { useRef, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import type { TeamSide } from '@/views/ValueDashboard/types';
import {
  novigDraftKey,
  presentNovigFeed,
  type NovigAccountReadiness,
  type NovigBoardQuote,
} from '@/lib/novig/presenter';
import { NovigManualTicket, type NovigSeededPrice } from './NovigManualTicket';

export interface NovigVenueSectionProps {
  quote: NovigBoardQuote;
  account: NovigAccountReadiness;
  nowMs: number;
  side: TeamSide;
  /** A board click's probability for an exact outcome, keyed by the click.
   * It seeds only that outcome's current draft, once. */
  initialSeed?: { key: string; outcomeId: string; probability: string } | null;
}

export function NovigVenueSection({ quote, account, nowMs, side, initialSeed = null }: NovigVenueSectionProps) {
  const target = quote[side];
  const draftKey = target ? novigDraftKey(account, target) : '';
  const [seed, setSeed] = useState<NovigSeededPrice | null>(null);
  const clicks = useRef(0);
  const [seedScope, setSeedScope] = useState({ draftKey: '', initialKey: '' });
  // Render-phase resets (React's "adjust state on prop change"): a new draft
  // drops any earlier click, and a new board click seeds only its own outcome.
  const initialKey = initialSeed?.key ?? '';
  if (seedScope.draftKey !== draftKey || seedScope.initialKey !== initialKey) {
    const freshClick = seedScope.initialKey !== initialKey;
    setSeedScope({ draftKey, initialKey });
    setSeed(freshClick && initialSeed && target && initialSeed.outcomeId === target.outcomeId
      ? { key: initialSeed.key, draftKey, probability: initialSeed.probability } : null);
  }
  if (!target) {
    return <p className="p-3 text-xs text-muted-foreground" data-testid="novig-no-outcome">No Novig outcome for this team.</p>;
  }
  const blocked = presentNovigFeed(quote.feed, nowMs).blocksPlacement;
  const level = (label: string, probability: string | null) => (
    <button type="button" disabled={blocked || probability === null}
      data-testid={`novig-level-${label.toLowerCase()}`}
      title={blocked ? 'Novig quote not current' : undefined}
      onClick={() => probability && setSeed({ key: `level:${++clicks.current}`, draftKey, probability })}
      className={cn('bg-muted min-w-0 flex-1 rounded-md px-2 py-1 text-xs tabular-nums',
        'disabled:cursor-not-allowed disabled:opacity-50 enabled:hover:text-foreground')}>
      <span className="text-muted-foreground mr-1 text-[10px]">{label}</span>
      {probability ?? '—'}
    </button>
  );
  return (
    <div data-testid="novig-venue-section">
      <div className="flex gap-1.5 px-3 pt-2">
        {level('Bid', target.bestBid)}
        {level('Ask', target.bestAsk)}
      </div>
      <NovigManualTicket target={target} feed={quote.feed} account={account} nowMs={nowMs} seededPrice={seed} />
    </div>
  );
}
